import React, { useCallback, useState } from 'react';

import InlineButton from 'components/InlineButton';

import useUnifiedDisclosures, {
  UnifiedDisclosuresError,
} from 'hooks/requests/unifiedDisclosures';
import catchOnly from 'utils/catchOnly';
import { NonClientError } from 'hooks/requests/fetchUtils';

type DisclosureLinkValues =
  | {
      paymentAgreementId: string;
      type:
        | 'loan-agreement'
        | 'loan-agreement-sp'
        | 'credit-score-notice'
        | 'tila'
        | 'payment-full-terms';
      onError: () => void;
      setLoadingState: (type: string, loading: boolean) => void;
    }
  | {
      paymentAgreementId: string;
      content: string;
      onError: () => void;
      setLoadingState: (type: string, loading: boolean) => void;
    };

const DisclosureLink: React.FC<DisclosureLinkValues> = props => {
  const { paymentAgreementId, children, onError, setLoadingState } = props;
  const [unifiedDisclosureRequest] = useUnifiedDisclosures();

  const [cachedContent, setCachedContent] = useState<string | null>(null);

  const getDisclosureResult = useCallback(async () => {
    if ('content' in props) {
      return props.content;
    }
    if (cachedContent) {
      return cachedContent;
    }

    try {
      if ('type' in props) {
        setLoadingState(props.type, true);
        const { disclosureContent } = await unifiedDisclosureRequest({
          paymentAgreementId,
          type: props.type,
        });
        // Save the content for future use
        setCachedContent(disclosureContent);
        return disclosureContent;
      }
    } catch (e) {
      if (catchOnly(e, NonClientError, UnifiedDisclosuresError)) {
        onError();
        return;
      }
    } finally {
      setLoadingState(props.type, false);
    }
  }, [
    props,
    cachedContent,
    setLoadingState,
    unifiedDisclosureRequest,
    paymentAgreementId,
    onError,
  ]);

  const handleClick = useCallback(
    async (event: React.MouseEvent<HTMLElement>) => {
      //prevent the checkbox from toggling when a link is clicked
      event.preventDefault();
      const win = window.open();
      if (!win) {
        onError();
        return;
      }
      const content = await getDisclosureResult();
      if (content) {
        win.document.body.innerHTML = content;
      } else {
        win.close();
      }
    },
    [getDisclosureResult, onError],
  );

  return <InlineButton onClick={handleClick}>{children}</InlineButton>;
};

export default DisclosureLink;
